import { useState } from "react";
import { MarketMaker } from "../../../../api/apiTypes";
import { Link } from "react-router-dom";
import { routesMap } from "../../../../routes/AppRoutes";
import Paginator from '../../../../utils/Paginator';
import ReportStatusBadge from "../../../../utils/ReportStatusBadge";
import { Eye } from 'lucide-react';

type ContainerListaDriverPageProps = {
  intermediari: MarketMaker[];
  driverRoute: string | undefined;
  reportId: string;
  idReport: string;
  driverId: string;
};

const tipiMercati = [
  { key: "equity", label: "Azioni" },
  { key: "bonds", label: "Obbligazioni" },
  { key: "derivatives", label: "Derivati" },
  { key: "etf", label: "ETF" },
  { key: "etc_etn", label: "ETC_ETN" },
  { key: "warrants", label: "Warrants" },
  { key: "covered_warrants", label: "Covered Warrants" },
] as const;

const ContainerMarketMaker = ({
  reportId,
  intermediari,
  driverRoute,
  idReport,
  driverId,
}: ContainerListaDriverPageProps) => {
  const itemsPerPage = 6;
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const saved = localStorage.getItem("marketMakerPage");
    return saved ? parseInt(saved, 10) : 1; // Corretto: base da 6 a 10
  });

  const totalPages = Math.ceil(intermediari.length / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    localStorage.setItem("marketMakerPage", page.toString());
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = intermediari.slice(startIndex, startIndex + itemsPerPage);

  // Definizione della griglia con 14 colonne per allinearsi con i 14 elementi
  const gridTemplate = "grid-cols-[2fr_1fr_repeat(7,1fr)_1fr_1fr_1fr_1fr_0.8fr]";

  return (
    <div className="">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Header tabella */}
        <div className={`grid ${gridTemplate} gap-x-2 p-4 border-b border-gray-100 bg-gray-50 font-semibold text-[#1B365D] text-xs`}>
          <div className="flex items-center">Intermediari</div>
          <div className="flex justify-center items-center">Market Maker</div>
          {tipiMercati.map(({ label }) => (
            <div key={label} className="flex justify-center items-center">
              {label}
            </div>
          ))}
          <div className="flex justify-center items-center">Totale mercati</div>
          <div className="flex justify-center items-center">Score</div>
          <div className="flex justify-center items-center">Stato</div>
          <div className="flex justify-center items-center">Validazione</div>
          <div /> {/* Questo div è vuoto e serve per allineare l'ultima colonna */}
        </div>

        {/* Body tabella */}
        {currentItems.map((intermediario) => {
          const { id, name, value, status, validation } = intermediario;
          const totaleMercati =
            (value.equity ? 1 : 0) +
            (value.bonds ? 1 : 0) +
            (value.derivatives ? 1 : 0) +
            (value.etf ? 1 : 0) +
            (value.etc_etn ? 1 : 0) +
            (value.warrants ? 1 : 0) +
            (value.covered_warrants ? 1 : 0);

          return (
            <div
              key={id}
              className={`grid ${gridTemplate} gap-x-2 p-4 hover:bg-gray-50/50 transition duration-150 ease-in-out text-gray-800 text-xs`}
            >
              <div className="flex items-center font-medium">{name}</div>
              <div className="flex justify-center items-center">
                {value.market_maker ? "SI" : "NO"}
              </div>

              {/* Tipi mercati */}
              {tipiMercati.map(({ key }) => (
                <div key={key} className="flex justify-center items-center">
                  {value[key] ? "V" : "-"}
                </div>
              ))}
              {/* Totale mercati */}
              <div className="flex justify-center items-center font-semibold">
                {totaleMercati}
              </div>
              {/* Score */}
              <div className="flex justify-center items-center font-semibold">
                {intermediario.value.score.total_score}
              </div>
              {/* Stato */}
              <div className="flex justify-center items-center">
                <ReportStatusBadge stato={status} />
              </div>
              {/* Validazione */}
              <div className="flex justify-center items-center">
                <ReportStatusBadge stato={validation} />
              </div>
              <div className="flex justify-center items-center">
                <Link
                  to={routesMap.VISUALIZZA_INTERMEDIARIO(
                    reportId,
                    driverRoute ?? "",
                    intermediario.name
                  )}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#1B365D] text-white hover:bg-[#142a4a] transition-colors"
                  state={{ idReport, driverId, key: intermediario.id }}
                >
                  <Eye className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          );
        })}
        
        {/* Paginazione */}
        {totalPages > 1 && (
          <div className="p-4">
            <Paginator
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContainerMarketMaker;